import { ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { BalanceCard } from '@/components/BalanceCard';
import { ClaimDetailsModal } from '@/components/ClaimDetailsModal';
import { ClaimSubmissionModal } from '@/components/ClaimSubmissionModal';
import { ClaimsList } from '@/components/ClaimsList';
import { ErrorDisplay } from '@/components/ErrorDisplay';
import { Header } from '@/components/Header';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageContainer } from '@/components/PageContainer';

import { categories } from '@/constants/categories';
import { CLAIM_SUGGESTIONS } from '@/constants/claimSuggestions';

import { useAuth } from '@/hooks/useAuth';
import { useClaims } from '@/hooks/useClaims';
import { useDashboardData } from '@/hooks/useDashboardData';

import type { IClaim } from '@/types/claim';

import { formatCurrency, formatDateFriendly } from '@/utils/formatting';

const RECENT_CLAIMS_COUNT = 5;
const ANIMATION_COMPLETION_DELAY_MS = 1150;

function Dashboard() {
  const { user, signOut } = useAuth();
  const { data: dashboardData, loading, error, refetch } = useDashboardData();
  const { data: claims } = useClaims({ limit: RECENT_CLAIMS_COUNT });
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [selectedClaim, setSelectedClaim] = useState<IClaim | null>(null);
  const [editingClaim, setEditingClaim] = useState<IClaim | null>(null);
  const hasAnimatedRef = useRef(false);

  // Pick 3 random suggestions on mount
  const [randomSuggestions] = useState(() => {
    const shuffled = [...CLAIM_SUGGESTIONS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3).map((suggestion) => ({
      ...suggestion,
      category: categories.find((c) => c.id === suggestion.categoryId),
    }));
  });

  const currentSuggestion = randomSuggestions[activeSuggestionIndex];

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setIsVisible(true);
    const timer = setTimeout(() => {
      hasAnimatedRef.current = true;
    }, ANIMATION_COMPLETION_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Rotate suggestions every 4 seconds
  useEffect(() => {
    if (!randomSuggestions.length) return;

    const interval = setInterval(() => {
      setActiveSuggestionIndex((prev) => (prev + 1) % randomSuggestions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [randomSuggestions]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner message="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={refetch} onLogout={signOut} />;
  }

  if (!dashboardData || !user) {
    return null;
  }

  const { balance, nextTopUpDate, monthlyUsage, monthlyLimit, monthlyResetDate } = dashboardData;
  const usagePercentage = monthlyLimit > 0 ? Math.min((monthlyUsage / monthlyLimit) * 100, 100) : 0;
  const monthlyRemaining = Math.max(0, monthlyLimit - monthlyUsage);

  // oxlint-disable-next-line react/refs
  const hasAnimated = hasAnimatedRef.current;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50" data-testid="dashboard">
      <Header user={user} onSignOut={signOut} />
      <PageContainer className="flex flex-1 flex-col pb-20 lg:pb-0">
        {/* Welcome Section */}
        <div className="mb-6">
          <h1 className="font-heading text-xl font-bold text-gray-900">
            Welcome back, {user.name.split(' ')[0]}!
          </h1>
          <p className="text-sm text-gray-600" data-testid="welcome-message">
            Unlock your potential with Unleash, powered by X-Team
          </p>
        </div>

        {/* Desktop: Two cards side by side / Mobile: Stacked */}
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Balance Card - Credit Card Style */}
          <BalanceCard
            balance={balance}
            nextTopUpDate={nextTopUpDate}
            hasAnimated={hasAnimated}
            isVisible={isVisible}
          />

          {/* Stay Energized Card - Desktop Only */}
          <div
            className={`hidden flex-col rounded-xl bg-linear-to-br from-white to-red-50/60 p-6 shadow-lg transition-all duration-700 lg:flex ${
              hasAnimated
                ? ''
                : isVisible
                  ? 'translate-y-0 opacity-100'
                  : '-translate-y-4 opacity-0'
            }`}
          >
            {/* Title */}
            <p className="mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
              Stay Energized
            </p>

            {/* Icon + Suggestion Row - Fixed height container */}
            <div className="flex min-h-[72px] items-start gap-4">
              {currentSuggestion?.category?.icon && (
                <div
                  className={`flex size-12 shrink-0 items-center justify-center rounded-lg shadow-md ${currentSuggestion.category.iconBg}`}
                >
                  <currentSuggestion.category.icon
                    className={`size-6 ${currentSuggestion.category.iconText}`}
                  />
                </div>
              )}
              <p
                key={activeSuggestionIndex}
                className="animate-fade-in text-lg font-medium text-gray-900"
              >
                {currentSuggestion?.text}
              </p>
            </div>

            {/* Dot Indicators */}
            <div className="mb-auto flex justify-center gap-1.5">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  onClick={() => setActiveSuggestionIndex(index)}
                  className={`size-1.5 rounded-full transition-colors duration-200 ${
                    index === activeSuggestionIndex ? 'bg-gray-700' : 'bg-gray-300'
                  }`}
                  aria-label={`View suggestion ${index + 1}`}
                />
              ))}
            </div>

            {/* New Claim Button */}
            <button
              onClick={() => setShowClaimModal(true)}
              className="mx-auto mt-4 w-3/4 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-red-600"
            >
              + New Claim
            </button>
          </div>
        </div>

        {/* Monthly Usage Section Header */}
        <h2
          className={`mb-3 font-heading text-xl font-bold text-gray-900 transition-all delay-150 duration-700 ${
            hasAnimated ? '' : isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
          }`}
        >
          Monthly Usage
        </h2>

        {/* Monthly Usage Card */}
        <div
          className={`mb-6 rounded-lg bg-white px-6 py-5 shadow-md transition-all delay-150 duration-700 ${
            hasAnimated ? '' : isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
          }`}
        >
          {/* Headline row */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-base font-medium text-gray-500">
              <span className="font-semibold text-gray-900">
                {formatCurrency(monthlyRemaining, true)}
              </span>{' '}
              remaining
            </p>
            <p
              className="text-sm text-gray-600"
              data-testid="monthly-usage-used"
              data-monthly-usage={monthlyUsage}
              data-monthly-limit={monthlyLimit}
            >
              {formatCurrency(monthlyUsage, true)} used of {formatCurrency(monthlyLimit)}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-3 h-3 w-full rounded-full bg-gray-200">
            <div
              className="h-3 rounded-full bg-red-500 transition-all duration-300"
              style={{ width: `${usagePercentage}%` }}
            ></div>
          </div>

          {/* Bottom row */}
          <p className="text-right text-xs text-gray-500">
            Reset {formatDateFriendly(monthlyResetDate)}
          </p>
        </div>

        <div
          className={`flex items-center justify-between border-b border-gray-100 py-6 transition-all delay-300 duration-700 ${
            hasAnimated ? '' : isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
          }`}
        >
          <h2 className="font-heading text-xl font-bold text-gray-900">Recent Claims</h2>
          <Link
            to="/claims"
            className="flex items-center gap-1 text-sm font-semibold text-red-500 hover:text-red-600"
          >
            View All History
            <ChevronRight className="size-4" />
          </Link>
        </div>

        {/* Desktop: Card container / Mobile: No container */}
        <div
          data-testid="recent-claims"
          className={`mb-6 transition-all delay-300 duration-700 lg:rounded-lg lg:bg-white lg:shadow-md ${
            hasAnimated ? '' : isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
          }`}
        >
          {claims.length > 0 ? (
            <>
              <div className="lg:px-6">
                <ClaimsList
                  claims={claims}
                  hasAnimated={hasAnimated}
                  isVisible={isVisible}
                  onClaimClick={setSelectedClaim}
                />
              </div>
              <div className="border-t border-gray-100 py-4 text-center text-sm text-gray-500 lg:px-6">
                Showing {claims.length} most recent transactions
              </div>
            </>
          ) : (
            <div
              className="py-8 text-center text-gray-500 lg:px-6"
              data-testid="claims-empty-state"
            >
              No claims yet. Submit your first claim to get started!
            </div>
          )}
        </div>
      </PageContainer>

      {/* Mobile: Floating New Claim Button */}
      <div className="fixed inset-x-0 bottom-4 px-8 lg:hidden">
        <button
          onClick={() => setShowClaimModal(true)}
          className="w-full rounded-lg bg-red-500 px-6 py-2 text-sm font-semibold text-white shadow-lg transition-colors duration-200 hover:bg-red-600"
        >
          + New Claim
        </button>
      </div>

      {showClaimModal && <ClaimSubmissionModal onClose={() => setShowClaimModal(false)} />}

      {editingClaim && (
        <ClaimSubmissionModal claim={editingClaim} onClose={() => setEditingClaim(null)} />
      )}

      {selectedClaim && (
        <ClaimDetailsModal
          claim={selectedClaim}
          onClose={() => setSelectedClaim(null)}
          onEditClick={(claim) => {
            setSelectedClaim(null);
            setEditingClaim(claim);
          }}
        />
      )}
    </div>
  );
}

export { Dashboard };
